import { ErrorEvent, EventHint } from '@sentry/react';

const CHANNEL_REQUEST_TIMEOUT_ERROR = 'ChannelRequestTimeoutError';
const CHANNEL_REQUEST_ERROR = 'ChannelRequestError';
/** Message prefix of the service worker diagnostics report (`reportUnresponsiveServiceWorker`). */
const SERVICE_WORKER_DIAGNOSTICS_MESSAGE = 'Service worker did not respond';

interface ChannelRequestErrorLike {
  name: string;
  operation?: unknown;
  messageId?: unknown;
}

function asChannelRequestError(error: unknown): ChannelRequestErrorLike | null {
  if (
    error instanceof Error &&
    (error.name === CHANNEL_REQUEST_TIMEOUT_ERROR || error.name === CHANNEL_REQUEST_ERROR)
  ) {
    return error as ChannelRequestErrorLike;
  }
  return null;
}

function asNonEmptyString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value !== '' ? value : fallback;
}

/** Errors of service worker requests and the diagnostics report: always sent to Sentry. */
export function isChannelRequestEvent(event: ErrorEvent, hint?: EventHint): boolean {
  return (
    asChannelRequestError(hint?.originalException) !== null ||
    Boolean(event.message?.startsWith(SERVICE_WORKER_DIAGNOSTICS_MESSAGE))
  );
}

/**
 * Groups service worker request failures per operation instead of one issue
 * for all (they share a stack trace); errors also per translated message.
 * The error's own fields (diagnostics, requestId) come with `extraErrorDataIntegration`.
 */
export function applyChannelRequestFingerprint(event: ErrorEvent, hint?: EventHint): ErrorEvent {
  const error = asChannelRequestError(hint?.originalException);
  if (!error) {
    return event;
  }
  const operation = asNonEmptyString(error.operation, 'unknown');
  // The timeout key predates the rename; kept so existing Sentry issues keep grouping.
  event.fingerprint =
    error.name === CHANNEL_REQUEST_TIMEOUT_ERROR
      ? ['create-entity-promise-timeout', operation]
      : ['channel-request-error', operation, asNonEmptyString(error.messageId, 'none')];
  return event;
}
